import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }

    // `cancel` and `close` don't bubble, so catch them in the capture phase (this also survives the dialog being re-rendered)
    this.addEventListener('cancel', this.#handleCancel, { capture: true });
    this.addEventListener('close', this.#handleClose, { capture: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    this.removeEventListener('cancel', this.#handleCancel, { capture: true });
    this.removeEventListener('close', this.#handleClose, { capture: true });
    unlockScroll(this);
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Whether the dialog was opened by `showDialog()` and still needs its close cleanup.
   */
  #isShown = false;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const scrollY = window.scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      if (dialog.open) return;

      lockScroll(this, scrollY);

      dialog.showModal();
      this.#isShown = true;
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    dialog.close();
    this.#handleClose();
  };

  /**
   * Animates the close when the browser asks to close the dialog (Escape, the Android back gesture).
   * If the browser doesn't let the request be cancelled, the dialog closes straight away and the
   * `close` event does the cleanup.
   *
   * @param {Event} event - The cancel event.
   */
  #handleCancel = (event) => {
    if (event.target !== this.refs.dialog) return;

    event.preventDefault();
    this.closeDialog();
  };

  /**
   * Restores the page after the dialog closes, however it was closed: `closeDialog()`, a close request
   * from the browser, or `dialog.close()` called directly. Runs once per opening.
   *
   * @param {Event} [event] - The close event, when the browser closed the dialog.
   */
  #handleClose = (event) => {
    const { dialog } = this.refs;

    if (event && event.target !== dialog) return;
    if (!this.#isShown || dialog.open) return;

    this.#isShown = false;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    unlockScroll(this);

    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

/*
 * Page scroll lock shared by every open dialog, so closing one (say the welcome pop-up over the cart
 * drawer) doesn't unlock the page or lose its scroll position while another is still open.
 * It fixes the body in place: with `html { overflow: hidden }` instead, taps in the cart drawer
 * stopped registering in WebKit when the page was scrolled.
 */

/** @type {Set<Element>} */
const scrollLockOwners = new Set();

/** The page scroll position when the first dialog locked it. */
let lockedScrollY = 0;

/**
 * Locks page scrolling for an owner. The first owner fixes the body at the current scroll position.
 * @param {Element} owner - The element that owns the lock.
 * @param {number} scrollY - The page scroll position.
 */
function lockScroll(owner, scrollY) {
  if (scrollLockOwners.has(owner)) return;

  scrollLockOwners.add(owner);
  if (scrollLockOwners.size > 1) return;

  lockedScrollY = scrollY;
  document.body.style.width = '100%';
  document.body.style.position = 'fixed';
  document.body.style.top = `-${scrollY}px`;
}

/**
 * Releases an owner's lock. The last owner restores the body and the scroll position.
 * @param {Element} owner - The element that owns the lock.
 */
function unlockScroll(owner) {
  if (!scrollLockOwners.delete(owner) || scrollLockOwners.size > 0) return;

  document.body.style.width = '';
  document.body.style.position = '';
  document.body.style.top = '';
  window.scrollTo({ top: lockedScrollY, behavior: 'instant' });
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
